import React, { useEffect, useRef, useState } from 'react';

/* ---- parse the vapc box (Tencent VAP config) from mp4 bytes ---- */
function extractJSON(b, start) {
  let i = start;
  while (i < b.length && b[i] !== 0x7b) i++;
  if (i >= b.length) return null;
  let depth = 0, inStr = false, esc = false, j = i;
  for (; j < b.length; j++) {
    const c = b[j];
    if (inStr) { if (esc) esc = false; else if (c === 0x5c) esc = true; else if (c === 0x22) inStr = false; }
    else { if (c === 0x22) inStr = true; else if (c === 0x7b) depth++; else if (c === 0x7d) { depth--; if (depth === 0) { j++; break; } } }
  }
  let s = '';
  for (let k = i; k < j; k++) s += String.fromCharCode(b[k]);
  try { return JSON.parse(s); } catch (e) { return null; }
}
function is4(a) { return Array.isArray(a) && a.length === 4; }

export function parseVapc(buf) {
  const b = new Uint8Array(buf);
  for (let i = 0; i < b.length - 4; i++) {
    if (b[i] === 0x76 && b[i + 1] === 0x61 && b[i + 2] === 0x70 && b[i + 3] === 0x63) { // 'vapc'
      let v = extractJSON(b, i + 4);
      if (v && v.info) v = v.info;               // VAP wraps config in {"info":{...}}
      if (v && is4(v.rgbFrame) && is4(v.aFrame)) return v;
    }
  }
  return null;
}

const VS = `attribute vec2 aPos; varying vec2 vUV;
void main(){ vUV=vec2((aPos.x+1.0)/2.0,(1.0-aPos.y)/2.0); gl_Position=vec4(aPos,0.0,1.0);}`;
const FS = `precision mediump float; varying vec2 vUV; uniform sampler2D uTex;
uniform vec2 uV; uniform vec4 uRGB; uniform vec4 uA;
void main(){ vec2 rc=(uRGB.xy+vUV*uRGB.zw)/uV; vec2 ac=(uA.xy+vUV*uA.zw)/uV;
  vec3 c=texture2D(uTex,rc).rgb; float a=texture2D(uTex,ac).r;
  gl_FragColor=vec4(c,a);}`;

/**
 * Plays a Tencent-VAP mp4 transparently on a WebGL canvas (over a checkerboard),
 * and emits a composited mid-frame thumbnail (webp data URL) via onThumbnail.
 * Props: file (File), width, height, onReady(cfg), onThumbnail(dataUrl), onError(msg)
 */
export default function VapPreview({ file, width = 120, height = 200, onReady, onThumbnail, onError }) {
  const canvasRef = useRef(null);
  const videoElRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!file) return;
    const canvas = canvasRef.current;
    const video = document.createElement('video');
    video.muted = true; video.loop = true; video.playsInline = true;
    videoElRef.current = video;
    setMuted(true);

    let gl = null, prog = null, tex = null, cfg = null;
    let raf = 0, cancelled = false, thumbDone = false;
    const url = URL.createObjectURL(file);

    const sh = (t, src) => { const s = gl.createShader(t); gl.shaderSource(s, src); gl.compileShader(s); return s; };
    function setupGL() {
      gl = canvas.getContext('webgl', { premultipliedAlpha: false, alpha: true, preserveDrawingBuffer: true });
      if (!gl) { onError && onError('WebGL not available'); return false; }
      prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog); gl.useProgram(prog);
      const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, 1, -1, -1, 1, 1, 1, -1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'aPos'); gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      tex = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.clearColor(0, 0, 0, 0);
      return true;
    }
    function drawFrame() {
      if (!gl || !cfg || video.readyState < 2 || !video.videoWidth) return;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);
      gl.viewport(0, 0, canvas.width, canvas.height); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(gl.getUniformLocation(prog, 'uV'), cfg.videoW, cfg.videoH);
      gl.uniform4f(gl.getUniformLocation(prog, 'uRGB'), cfg.rgbFrame[0], cfg.rgbFrame[1], cfg.rgbFrame[2], cfg.rgbFrame[3]);
      gl.uniform4f(gl.getUniformLocation(prog, 'uA'), cfg.aFrame[0], cfg.aFrame[1], cfg.aFrame[2], cfg.aFrame[3]);
      gl.uniform1i(gl.getUniformLocation(prog, 'uTex'), 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    function loop() {
      if (cancelled) return;
      raf = requestAnimationFrame(loop);
      drawFrame();
    }

    file.arrayBuffer().then((buf) => {
      if (cancelled) return;
      const v = parseVapc(buf);
      video.src = url;
      video.onloadedmetadata = () => {
        if (cancelled) return;
        if (v) cfg = { videoW: v.videoW || video.videoWidth, videoH: v.videoH || video.videoHeight, rgbFrame: v.rgbFrame, aFrame: v.aFrame };
        else { const W = video.videoWidth, H = video.videoHeight; cfg = { videoW: W, videoH: H, rgbFrame: [0, 0, W / 2, H], aFrame: [W / 2, 0, W / 2, H] }; }
        canvas.width = Math.max(2, cfg.rgbFrame[2]);
        canvas.height = Math.max(2, cfg.rgbFrame[3]);
        if (!setupGL()) return;
        onReady && onReady(cfg);
        // capture a mid-animation frame as thumbnail, then play/loop for preview
        const t = video.duration && isFinite(video.duration) ? Math.min(video.duration * 0.5, video.duration - 0.05) : 0;
        video.currentTime = t > 0 ? t : 0;
      };
      video.onseeked = () => {
        if (cancelled) return;
        if (!thumbDone) {
          thumbDone = true;
          drawFrame();
          try { onThumbnail && onThumbnail(canvas.toDataURL('image/webp', 0.85)); } catch (e) { /* noop */ }
          video.currentTime = 0;
          video.play().catch(() => {});
          loop();
        }
      };
      video.onerror = () => onError && onError('Could not load VAP video (codec?)');
    }).catch((e) => onError && onError(e.message));

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      try { video.pause(); } catch (e) { /* noop */ }
      video.src = '';
      URL.revokeObjectURL(url);
    };
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => {
    const v = videoElRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    v.play().catch(() => {}); // click is a user gesture → unmuted playback allowed
  };

  const checker = {
    width, height, borderRadius: 6, display: 'block',
    backgroundImage:
      'linear-gradient(45deg,#3a3f52 25%,transparent 25%),linear-gradient(-45deg,#3a3f52 25%,transparent 25%),' +
      'linear-gradient(45deg,transparent 75%,#3a3f52 75%),linear-gradient(-45deg,transparent 75%,#3a3f52 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
    objectFit: 'contain',
  };
  return (
    <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
      <canvas ref={canvasRef} style={checker} />
      <button
        type="button"
        onClick={toggleMute}
        title={muted ? 'Enable sound' : 'Mute'}
        style={{
          position: 'absolute', bottom: 5, right: 5,
          background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
          borderRadius: 6, fontSize: 13, lineHeight: 1, padding: '3px 7px', cursor: 'pointer',
        }}
      >
        {muted ? '🔇' : '🔊'}
      </button>
    </div>
  );
}
